import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Coffee, Zap, Star, User, Moon, Clock, Check, RefreshCw } from "lucide-react";
import PhoneShell from "../components/PhoneShell";
import {
    setPhone as saveSharedPhone,
    setName as saveSharedName,
    getName,
    setRef,
    clearCart,
    addToCart,
    getNotifyOk,
    setNotifyOk,
} from "../lib/session";
import { api, apiRaw } from "../lib/api";

const Landing = () => {
    const nav = useNavigate();
    const { slug } = useParams();
    const ctx = useOutletContext() || {};
    const tenant = ctx.tenant;

    const [val, setVal] = useState("");
    const [nameVal, setNameVal] = useState(getName());
    const [notifyOk, setNotifyOkState] = useState(getNotifyOk());
    const [favs, setFavs] = useState([]);
    const [status, setStatus] = useState(null);
    const [identity, setIdentity] = useState(null); // {exists, name} or null
    const [identityConfirmed, setIdentityConfirmed] = useState(false);

    // Capture ?ref= for bring-a-friend
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get("ref");
        if (ref && /^\d{4}$/.test(ref)) setRef(ref);
    }, []);

    const digits = val.replace(/\D/g, "");
    const valid = digits.length >= 8 && digits.length <= 15;

    useEffect(() => {
        if (!tenant) return;
        setStatus(tenant.status_info || null);
    }, [tenant]);

    // Debounced identity lookup (cross-tenant) — privacy-first
    useEffect(() => {
        if (!valid) {
            setIdentity(null);
            setIdentityConfirmed(false);
            return;
        }
        setIdentityConfirmed(false);
        const t = setTimeout(() => {
            apiRaw
                .get(`/identity/${digits}`)
                .then((r) => setIdentity(r.data))
                .catch(() => setIdentity(null));
        }, 400);
        return () => clearTimeout(t);
    }, [digits, valid]);

    // Auto-load favourites for this tenant if valid phone
    useEffect(() => {
        if (!valid || (status && !status.is_open)) {
            setFavs([]);
            return;
        }
        const t = setTimeout(() => {
            api.get(`/favourites/${digits}`)
                .then((r) => setFavs(r.data.favourites || []))
                .catch(() => setFavs([]));
        }, 400);
        return () => clearTimeout(t);
    }, [digits, valid, status]);

    const confirmIdentity = () => {
        if (identity?.name) {
            setNameVal(identity.name);
            saveSharedName(identity.name);
        }
        if (identity?.notify_ok) {
            setNotifyOkState(true);
            setNotifyOk(true);
        }
        setIdentityConfirmed(true);
    };

    const rejectIdentity = () => {
        setNameVal("");
        setIdentity(null); // dismiss the prompt
        setIdentityConfirmed(true); // treat as fresh user
    };

    const handleContinue = () => {
        if (!valid || (status && !status.is_open)) return;
        saveSharedPhone(digits);
        saveSharedName(nameVal.trim());
        setNotifyOk(notifyOk);
        nav(`/${slug}/menu`);
    };

    const handleReorderUsual = (fav) => {
        if (!valid || (status && !status.is_open)) return;
        saveSharedPhone(digits);
        saveSharedName(nameVal.trim());
        setNotifyOk(notifyOk);
        clearCart();
        addToCart({
            id: fav.item_id,
            name: fav.name,
            kind: fav.kind,
            size: fav.size || null,
            qty: 1,
            price: fav.price,
            customizations: fav.customizations || null,
        });
        nav(`/${slug}/cart`);
    };

    const topFav = favs[0];
    const isClosed = status && !status.is_open;
    const alwaysOpen = status?.always_open;
    const cafeName = tenant?.name || "Coffeein7";
    const showIdentity = identity?.exists && !identityConfirmed;

    return (
        <PhoneShell>
            <div className="relative min-h-screen flex flex-col px-6 pt-14 pb-10">
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex items-center gap-2 self-start rounded-full px-3 py-1.5 border shadow-[0_4px_20px_rgba(44,26,18,0.04)] ${
                        isClosed
                            ? "bg-[#2C1A12] text-white border-[#2C1A12]"
                            : "bg-white text-[#2C1A12] border-[#E8DCCB]"
                    }`}
                    data-testid="shop-status-badge"
                >
                    {isClosed ? (
                        <>
                            <Moon className="h-3 w-3" strokeWidth={2.5} />
                            <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">
                                Closed · Opens {status?.opens_at_label || "7:00 AM"}
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="h-2 w-2 rounded-full bg-[#1B995E] animate-pulse" />
                            <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">
                                {alwaysOpen
                                    ? `${cafeName} · Order Ahead`
                                    : `Open · Until ${status?.closes_at_label || "12:00 PM"}`}
                            </span>
                        </>
                    )}
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                    className="mt-14"
                >
                    <div className="text-[10px] uppercase tracking-[0.35em] text-[#8C7A72] font-bold">
                        Powered by Coffeein7
                    </div>
                    <h1 className="mt-2 font-display text-5xl sm:text-6xl font-black leading-[0.9] text-[#2C1A12]">
                        {cafeName}
                    </h1>
                    <p className="mt-4 text-[#8C7A72] text-lg leading-snug max-w-[20ch]">
                        Skip the queue. Your daily fix in seven minutes flat.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="mt-14"
                >
                    <label
                        htmlFor="phone"
                        className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8C7A72]"
                    >
                        Your mobile number
                    </label>
                    <input
                        id="phone"
                        data-testid="phone-input"
                        type="tel"
                        inputMode="numeric"
                        placeholder="0400 000 000"
                        value={val}
                        onChange={(e) => setVal(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !showIdentity && handleContinue()}
                        disabled={isClosed}
                        className={`mt-3 w-full text-4xl font-display font-black text-[#2C1A12] placeholder:text-[#E8DCCB] bg-transparent border-b-4 border-[#E8DCCB] focus:border-[#FF4F18] outline-none py-3 transition-colors ${
                            isClosed ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                    />
                    <p className="mt-2 text-xs text-[#8C7A72]">
                        {isClosed
                            ? `We're closed right now. Come back at ${status?.opens_at_label || "7:00 AM"}.`
                            : "No passwords. Your number is your account."}
                    </p>
                </motion.div>

                {/* Privacy-first identity confirmation */}
                <AnimatePresence>
                    {showIdentity && (
                        <motion.div
                            key="identity-card"
                            initial={{ opacity: 0, y: 8, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: "auto" }}
                            exit={{ opacity: 0, y: -4, height: 0 }}
                            className="mt-6 overflow-hidden"
                            data-testid="identity-confirm-card"
                        >
                            <div className="rounded-3xl bg-white border-2 border-[#FF4F18]/40 p-4 shadow-[0_12px_32px_rgba(255,79,24,0.08)]">
                                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                                    Is this you?
                                </div>
                                <div className="mt-1 font-display font-black text-2xl">
                                    {identity.name} 👋
                                </div>
                                <p className="mt-1 text-xs text-[#8C7A72]">
                                    We recognise your number from Coffeein7. Confirm to continue as {identity.name}.
                                </p>
                                <div className="mt-4 flex gap-2">
                                    <button
                                        data-testid="identity-confirm-yes"
                                        onClick={confirmIdentity}
                                        className="flex-1 py-3 rounded-full bg-[#FF4F18] text-white font-display font-bold text-sm active:scale-95 flex items-center justify-center gap-1.5"
                                    >
                                        <Check className="h-4 w-4" strokeWidth={3} />
                                        Yes, that's me
                                    </button>
                                    <button
                                        data-testid="identity-confirm-no"
                                        onClick={rejectIdentity}
                                        className="flex-1 py-3 rounded-full bg-white text-[#2C1A12] border-2 border-[#E8DCCB] font-display font-bold text-sm active:scale-95 flex items-center justify-center gap-1.5"
                                    >
                                        <RefreshCw className="h-3.5 w-3.5" strokeWidth={2.5} />
                                        Not me
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Name field (only shown when identity confirmed OR when no known identity) */}
                {(!showIdentity || !identity?.exists) && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.18 }}
                        className="mt-6"
                    >
                        <label
                            htmlFor="cup-name"
                            className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8C7A72] flex items-center gap-1.5"
                        >
                            <User className="h-3 w-3" strokeWidth={2.5} />
                            Name on the cup{" "}
                            <span className="text-[#E8DCCB] normal-case tracking-normal">— optional</span>
                        </label>
                        <input
                            id="cup-name"
                            data-testid="name-input"
                            type="text"
                            maxLength={30}
                            placeholder="e.g. Sam"
                            value={nameVal}
                            onChange={(e) => setNameVal(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleContinue()}
                            disabled={isClosed}
                            className={`mt-2 w-full text-2xl font-display font-bold text-[#2C1A12] placeholder:text-[#E8DCCB] bg-white border-2 border-[#E8DCCB] rounded-2xl px-4 py-3 focus:border-[#FF4F18] outline-none transition-colors ${
                                isClosed ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                        />
                    </motion.div>
                )}

                {/* Consent checkbox */}
                {!showIdentity && (
                    <motion.label
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.22 }}
                        htmlFor="notify-ok"
                        className="mt-4 flex items-start gap-3 cursor-pointer select-none"
                        data-testid="notify-consent-label"
                    >
                        <input
                            id="notify-ok"
                            data-testid="notify-consent"
                            type="checkbox"
                            checked={notifyOk}
                            onChange={(e) => setNotifyOkState(e.target.checked)}
                            className="mt-1 h-4 w-4 accent-[#FF4F18] shrink-0"
                        />
                        <span className="text-xs text-[#8C7A72] leading-relaxed">
                            Send me offers and free-coffee reminders from {cafeName}. Unsubscribe anytime.
                        </span>
                    </motion.label>
                )}

                <AnimatePresence>
                    {topFav && identityConfirmed && (
                        <motion.button
                            key="reorder-usual"
                            initial={{ opacity: 0, y: 12, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -8 }}
                            data-testid="reorder-usual-btn"
                            onClick={() => handleReorderUsual(topFav)}
                            className="mt-8 w-full text-left rounded-3xl bg-[#2C1A12] text-white p-5 relative overflow-hidden active:scale-[0.98] transition-transform shadow-[0_16px_40px_rgba(44,26,18,0.32)]"
                        >
                            <div className="absolute -right-10 -bottom-10 h-40 w-40 rounded-full bg-[#FF4F18]/30" />
                            <div className="absolute right-6 top-6">
                                <div className="h-10 w-10 rounded-full bg-[#FF4F18] flex items-center justify-center">
                                    <Zap className="h-5 w-5 fill-white" strokeWidth={2.5} />
                                </div>
                            </div>
                            <div className="relative">
                                <div className="flex items-center gap-1.5 text-[#FF4F18] text-[10px] uppercase tracking-[0.3em] font-bold">
                                    <Star className="h-3 w-3 fill-[#FF4F18]" strokeWidth={2} />
                                    {nameVal ? `Hey ${nameVal.trim().split(" ")[0]}` : "Welcome back"}
                                </div>
                                <div className="mt-2 font-display font-black text-2xl leading-tight">
                                    Your usual?
                                </div>
                                <div className="mt-1 text-white/90 font-semibold text-base">
                                    {topFav.name}
                                    {topFav.size ? ` · ${topFav.size === "L" ? "Large" : "Small"}` : ""}
                                </div>
                                {topFav.label && (
                                    <div className="text-white/60 text-xs mt-0.5">{topFav.label}</div>
                                )}
                                <div className="mt-4 inline-flex items-center gap-2 bg-white text-[#2C1A12] rounded-full pl-4 pr-3 py-2 font-display font-bold text-sm">
                                    Reorder · ${topFav.price.toFixed(2)}
                                    <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                                </div>
                                {favs.length > 1 && (
                                    <div className="mt-3 text-white/50 text-[10px] uppercase tracking-[0.2em]">
                                        +{favs.length - 1} more saved
                                    </div>
                                )}
                            </div>
                        </motion.button>
                    )}
                </AnimatePresence>

                <div className="flex-1" />

                <motion.button
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    data-testid="continue-btn"
                    onClick={handleContinue}
                    disabled={!valid || isClosed || showIdentity}
                    className={`w-full py-5 rounded-full font-display font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                        valid && !isClosed && !showIdentity
                            ? topFav
                                ? "bg-white text-[#2C1A12] border-2 border-[#E8DCCB] active:scale-[0.98]"
                                : "bg-[#FF4F18] text-white active:scale-[0.98] shadow-[0_12px_32px_rgba(255,79,24,0.32)]"
                            : "bg-[#E8DCCB] text-[#8C7A72] cursor-not-allowed"
                    }`}
                >
                    {isClosed ? (
                        <>
                            <Clock className="h-5 w-5" strokeWidth={2.5} />
                            Closed · See you at {status?.opens_at_label || "7:00 AM"}
                        </>
                    ) : (
                        <>
                            {topFav ? "Browse full menu" : "Continue"}
                            <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
                        </>
                    )}
                </motion.button>

                <div className="mt-6 flex items-center justify-center gap-2 text-[#8C7A72] text-xs">
                    <Coffee className="h-3.5 w-3.5" strokeWidth={2.5} />
                    <span>
                        {isClosed
                            ? `Hours: ${status?.opens_at_label || "7 AM"} – ${status?.closes_at_label || "12 PM"}`
                            : "Every 10th coffee is on us · Add to Home Screen"}
                    </span>
                </div>

                <button
                    data-testid="landing-rewards-link"
                    onClick={() => nav(`/${slug}/rewards`)}
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-[#FF4F18] text-xs font-display font-bold hover:underline"
                >
                    🫘 Spend your Beans on rewards →
                </button>

                <button
                    data-testid="landing-wallet-link"
                    onClick={() => nav(`/${slug}/wallet`)}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[#8C7A72] text-xs font-display font-bold hover:text-[#FF4F18]"
                >
                    💳 Add to Apple / Google Wallet →
                </button>
            </div>
        </PhoneShell>
    );
};

export default Landing;
